import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import {
  Modal,
  TouchableWithoutFeedback,
  View,
  useWindowDimensions,
  Platform,
  FlatList,
  ListRenderItem,
  ListRenderItemInfo,
} from 'react-native'
import type { Item } from 'react-native-picker-select'
import Typography from 'APP/Converse/Typography'
import { ShadowView } from 'APP/Converse/ShadowView'
import Infotip from 'APP/Converse/Inputs/Infotip'
import Icon from 'APP/Converse/Icon'
import { StyledInputContainer } from 'APP/Converse/Inputs/styles'
import { DropDownContainerView } from './DropDownContainerView'
import { DropDownProps, DropDownPosition, WebKeyboardEvent } from './types'
import {
  StyledDropdownContainer,
  StyledTriggerContainer,
  StyledTriggerText,
  StyledIconContainer,
  StyledDropdown,
  StyledOption,
  StyledSeparator,
  StyledAddItem,
  StyledWebScrollView,
} from './styles'

const DROPDOWN_ITEM_HEIGHT = 40 // Approximate item height (12px padding * 2 + ~16px text)
const DROPDOWN_MAX_HEIGHT = DROPDOWN_ITEM_HEIGHT * 5

// Helper function to reliably scroll to an index with fallback to offset-based scrolling
const scrollToIndexSafely = (
  flatListRef: React.RefObject<FlatList<Item> | null>,
  index: number,
  animated: boolean = true
) => {
  if (!flatListRef.current) return

  try {
    flatListRef.current.scrollToIndex({ index, animated })
  } catch (_) {
    // Fallback to offset-based scrolling if scrollToIndex fails
    const offset = index * DROPDOWN_ITEM_HEIGHT
    flatListRef.current.scrollToOffset({ offset, animated })
  }
}

const DropDown = ({
  value,
  label,
  showRequiredAsterisk,
  infotip,
  isPersistentInfotip,
  isFocused: isFocusedProp,
  error,
  disabled,
  onValueChange,
  items,
  testID,
  placeholder = '',
  addItemLabel,
  onAddItemPress,
}: DropDownProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [position, setPosition] = useState<DropDownPosition | null>(null)
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null)
  const containerRef = useRef<View>(null)
  const flatListRef = useRef<FlatList<Item>>(null)
  const dropdownContainerRef = useRef<View>(null)
  const retryCountRef = useRef(0)
  const isMeasuringRef = useRef(false)
  const prevWindowDimensionsRef = useRef({ width: 0, height: 0 })
  const { width: windowWidth, height: windowHeight } = useWindowDimensions()

  // Generate unique IDs for ARIA attributes
  const componentId = useRef(`dropdown-${Math.random().toString(36).substring(2, 11)}`).current
  const labelId = `${componentId}-label`
  const listboxId = `${componentId}-listbox`
  const getOptionId = useCallback(
    (index: number) => `${componentId}-option-${index}`,
    [componentId]
  )

  const isFocused = isExpanded || isFocusedProp

  const selectedItem = items.find((item) => item.value === value)
  const displayText = selectedItem?.label || placeholder

  // Helper function to safely measure and set position
  const measureAndSetPosition = useCallback((onSuccess?: () => void) => {
    if (!containerRef.current || isMeasuringRef.current) {
      onSuccess?.()
      return
    }

    isMeasuringRef.current = true
    const MAX_RETRIES = 5
    retryCountRef.current = 0

    const measure = () => {
      if (!containerRef.current) {
        isMeasuringRef.current = false
        onSuccess?.()
        return
      }

      try {
        containerRef.current.measureInWindow((x, y, width, height) => {
          // Validate that all values are valid numbers (not NaN or undefined)
          if (
            x !== undefined &&
            y !== undefined &&
            width !== undefined &&
            height !== undefined &&
            width > 0 &&
            height > 0
          ) {
            setPosition({ x, y, width, height })
            retryCountRef.current = 0
            isMeasuringRef.current = false
            onSuccess?.()
          } else {
            // Retry if measurement failed
            if (retryCountRef.current < MAX_RETRIES) {
              retryCountRef.current += 1
              setTimeout(
                () => {
                  measure()
                },
                Platform.OS === 'android' ? 100 : 50
              )
            } else {
              // Max retries reached, give up
              retryCountRef.current = 0
              isMeasuringRef.current = false
              onSuccess?.()
            }
          }
        })
      } catch (_error) {
        // If measureInWindow throws an error, retry
        if (retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current += 1
          setTimeout(
            () => {
              measure()
            },
            Platform.OS === 'android' ? 100 : 50
          )
        } else {
          retryCountRef.current = 0
          isMeasuringRef.current = false
          onSuccess?.()
        }
      }
    }

    // On Android, ensure the view is laid out before measuring
    if (Platform.OS === 'android') {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          measure()
        })
      })
    } else {
      measure()
    }
  }, [])

  useEffect(() => {
    if (isExpanded) {
      // Re-measure when window dimensions change while expanded
      const prevDimensions = prevWindowDimensionsRef.current
      const dimensionsChanged =
        prevDimensions.width !== windowWidth || prevDimensions.height !== windowHeight

      if (dimensionsChanged && !isMeasuringRef.current) {
        prevWindowDimensionsRef.current = { width: windowWidth, height: windowHeight }
        measureAndSetPosition()
      }
    } else {
      // Reset previous dimensions when collapsed
      prevWindowDimensionsRef.current = { width: windowWidth, height: windowHeight }
    }
  }, [isExpanded, windowWidth, windowHeight, measureAndSetPosition])

  const handleOpen = useCallback(() => {
    if (disabled) return

    // Initialize focused index to currently selected item, or null if none (user will navigate with keyboard)
    const currentIndex = items.findIndex((item) => item.value === value)
    setFocusedIndex(currentIndex >= 0 ? currentIndex : null)

    // Measure position first, then set expanded only after we have valid position
    measureAndSetPosition(() => {
      setIsExpanded(true)
    })
  }, [disabled, items, value, measureAndSetPosition])

  // Handle onLayout to ensure view is ready before measuring
  const handleContainerLayout = useCallback(() => {
    // Only measure if we're expanded, don't have position yet, and not already measuring
    if (isExpanded && !position && !isMeasuringRef.current) {
      measureAndSetPosition()
    }
  }, [isExpanded, position, measureAndSetPosition])

  const handleClose = useCallback(() => {
    setIsExpanded(false)
    setFocusedIndex(null)
    setPosition(null)
  }, [])

  const handleSelectItem = useCallback(
    (itemValue: string) => {
      onValueChange(itemValue)
      handleClose()
    },
    [onValueChange, handleClose]
  )

  const handleAddItemPress = useCallback(() => {
    if (onAddItemPress) {
      onAddItemPress()
      handleClose()
    }
  }, [onAddItemPress, handleClose])

  // Handle keyboard navigation when combobox is closed
  const handleClosedKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (isExpanded || Platform.OS !== 'web' || items.length === 0 || disabled) return

      const key = event.key

      // Keys that open the listbox when combobox is closed
      const keysThatOpenListbox = ['ArrowDown', 'ArrowUp', 'Enter', ' ', 'Home']

      // Handle Alt + ArrowDown - opens listbox (check before general ArrowDown)
      if (key === 'ArrowDown' && event.altKey) {
        event.preventDefault()
        handleOpen()
        return
      }

      // Handle keys that open the listbox
      if (keysThatOpenListbox.includes(key)) {
        event.preventDefault()
        handleOpen()
        return
      }

      // Handle End - opens listbox and moves focus to last option
      if (key === 'End') {
        event.preventDefault()
        handleOpen()
        // Set focus to last item after opening - use requestAnimationFrame for reliable timing
        const totalItems = items.length + (addItemLabel && onAddItemPress ? 1 : 0)
        requestAnimationFrame(() => {
          const lastIndex = totalItems - 1
          setFocusedIndex(lastIndex)
          if (lastIndex < items.length) {
            scrollToIndexSafely(flatListRef, lastIndex)
          }
        })
        return
      }

      // Handle printable characters - opens listbox and moves focus to matching option
      if (key.length === 1 && key.match(/[a-zA-Z0-9]/)) {
        event.preventDefault()
        const matchingIndex = items.findIndex((item) =>
          item.label.toLowerCase().startsWith(key.toLowerCase())
        )
        if (matchingIndex >= 0) {
          handleOpen()
          requestAnimationFrame(() => {
            setFocusedIndex(matchingIndex)
            scrollToIndexSafely(flatListRef, matchingIndex)
          })
        } else {
          handleOpen()
        }
        return
      }
    },
    [
      isExpanded,
      items,
      disabled,
      handleOpen,
      flatListRef,
      setFocusedIndex,
      addItemLabel,
      onAddItemPress,
    ]
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (Platform.OS !== 'web' || items.length === 0) return

      // Handle closed state keyboard events
      if (!isExpanded) {
        handleClosedKeyDown(event)
        return
      }

      // Handle open state keyboard events
      const currentIndex = focusedIndex !== null ? focusedIndex : 0
      const key = event.key

      // Handle arrow keys for navigation
      const totalItems = items.length + (addItemLabel && onAddItemPress ? 1 : 0)
      if (key === 'ArrowDown') {
        event.preventDefault()
        const nextIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0
        setFocusedIndex(nextIndex)
        if (nextIndex < items.length) {
          scrollToIndexSafely(flatListRef, nextIndex)
        }
        return
      }

      // Handle Alt + ArrowUp - selects and closes (must come before regular ArrowUp)
      if (key === 'ArrowUp' && event.altKey) {
        event.preventDefault()
        if (currentIndex >= 0 && currentIndex < items.length && items[currentIndex]) {
          handleSelectItem(items[currentIndex].value)
        }
        return
      }

      if (key === 'ArrowUp') {
        event.preventDefault()
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1
        setFocusedIndex(prevIndex)
        if (prevIndex < items.length) {
          scrollToIndexSafely(flatListRef, prevIndex)
        }
        return
      }

      // Handle Enter or Space to select
      if (key === 'Enter' || key === ' ') {
        event.preventDefault()
        if (currentIndex >= 0 && currentIndex < items.length && items[currentIndex]) {
          handleSelectItem(items[currentIndex].value)
        } else if (currentIndex === items.length && addItemLabel && onAddItemPress) {
          handleAddItemPress()
        }
        return
      }

      // Handle Escape to close
      if (key === 'Escape') {
        event.preventDefault()
        handleClose()
        return
      }

      // Handle Tab - selects and closes
      if (key === 'Tab') {
        if (currentIndex >= 0 && currentIndex < items.length && items[currentIndex]) {
          handleSelectItem(items[currentIndex].value)
        } else if (currentIndex === items.length && addItemLabel && onAddItemPress) {
          handleAddItemPress()
        }
        return
      }

      // Handle Home - moves focus to first option
      if (key === 'Home') {
        event.preventDefault()
        setFocusedIndex(0)
        scrollToIndexSafely(flatListRef, 0)
        return
      }

      // Handle End - moves focus to last option
      if (key === 'End') {
        event.preventDefault()
        const totalItems = items.length + (addItemLabel && onAddItemPress ? 1 : 0)
        const lastIndex = totalItems - 1
        setFocusedIndex(lastIndex)
        if (lastIndex < items.length) {
          scrollToIndexSafely(flatListRef, lastIndex)
        }
        return
      }

      // Handle PageUp - jump up 10 options
      if (key === 'PageUp') {
        event.preventDefault()
        const newIndex = Math.max(0, currentIndex - 10)
        setFocusedIndex(newIndex)
        scrollToIndexSafely(flatListRef, newIndex)
        return
      }

      // Handle PageDown - jump down 10 options
      if (key === 'PageDown') {
        event.preventDefault()
        const totalItems = items.length + (addItemLabel && onAddItemPress ? 1 : 0)
        const newIndex = Math.min(totalItems - 1, currentIndex + 10)
        setFocusedIndex(newIndex)
        if (newIndex < items.length) {
          scrollToIndexSafely(flatListRef, newIndex)
        }
        return
      }

      // Handle printable characters - navigate to matching option
      if (key.length === 1 && key.match(/[a-zA-Z0-9]/)) {
        event.preventDefault()
        // Find next option starting with the typed character
        const startIndex = (currentIndex + 1) % items.length
        let matchingIndex = items.findIndex(
          (item, idx) => idx >= startIndex && item.label.toLowerCase().startsWith(key.toLowerCase())
        )
        if (matchingIndex === -1) {
          matchingIndex = items.findIndex((item) =>
            item.label.toLowerCase().startsWith(key.toLowerCase())
          )
        }
        if (matchingIndex >= 0) {
          setFocusedIndex(matchingIndex)
          scrollToIndexSafely(flatListRef, matchingIndex)
        }
        return
      }
    },
    [
      isExpanded,
      focusedIndex,
      items,
      handleSelectItem,
      handleClose,
      handleClosedKeyDown,
      addItemLabel,
      onAddItemPress,
      handleAddItemPress,
    ]
  )

  // Handle keyboard events for web - using onKeyDown prop on View
  const handleWebKeyDown = useCallback(
    (event: WebKeyboardEvent) => {
      if (Platform.OS !== 'web') return

      // React Native Web passes the key in nativeEvent.key or event.key
      // Prefer .key over .code as .code represents physical key, not logical key
      const rawKey = event.nativeEvent?.key || event.key
      if (!rawKey) return

      // Normalize key values to match standard KeyboardEvent.key values
      const normalizedKey =
        rawKey === 'Enter' ? 'Enter' : rawKey === 'Space' || rawKey === ' ' ? ' ' : rawKey

      // Convert to standard KeyboardEvent-like object
      const keyboardEvent = {
        key: normalizedKey,
        altKey: event.altKey || event.nativeEvent?.altKey || false,
        preventDefault: () => {
          try {
            if (event.preventDefault) event.preventDefault()
            if (event.nativeEvent?.preventDefault) event.nativeEvent.preventDefault()
            if (event.stopPropagation) event.stopPropagation()
          } catch (_) {
            // Ignore errors
          }
        },
      } as KeyboardEvent
      handleKeyDown(keyboardEvent)
    },
    [handleKeyDown]
  )

  // Also attach window listener as fallback for better event capture
  // Only attach when dropdown is expanded to avoid conflicts with onKeyDown prop
  useEffect(() => {
    if (Platform.OS !== 'web' || !isExpanded) return

    const handleKeyDownEvent = (event: KeyboardEvent) => {
      // Only handle if event hasn't been handled by onKeyDown prop
      // Check if the event target is within our dropdown container
      const target = event.target as HTMLElement
      if (dropdownContainerRef.current && target) {
        const containerElement = dropdownContainerRef.current as any
        if (containerElement && !containerElement.contains?.(target)) {
          return // Event is outside our component, let it bubble
        }
      }
      handleKeyDown(event)
    }

    window.addEventListener('keydown', handleKeyDownEvent, true)

    return () => {
      window.removeEventListener('keydown', handleKeyDownEvent, true)
    }
  }, [handleKeyDown, isExpanded])

  const renderItem = useCallback<ListRenderItem<Item>>(
    ({ item, index }) => {
      const isSelected = item.value === value
      const isFocused = focusedIndex === index
      const optionId = getOptionId(index)
      return (
        <TouchableWithoutFeedback
          onPress={() => handleSelectItem(item.value)}
          disabled={disabled}
          {...(Platform.OS === 'web' && {
            role: 'option',
            'aria-selected': isSelected,
            id: optionId,
            accessibilityRole: 'button',
            accessibilityState: { selected: isSelected, disabled },
          })}
        >
          <StyledOption focused={isFocused && Platform.OS === 'web'}>
            <Typography variant={isSelected ? 'bodyNormalBold' : 'bodyNormal'} color="text">
              {item.label}
            </Typography>
          </StyledOption>
        </TouchableWithoutFeedback>
      )
    },
    [value, disabled, handleSelectItem, focusedIndex, getOptionId]
  )

  const renderAddItem = useCallback(() => {
    if (!addItemLabel || !onAddItemPress) return null

    const isAddItemFocused = focusedIndex === items.length
    const addItemId = `${componentId}-option-add`

    return (
      <>
        <StyledSeparator />
        <TouchableWithoutFeedback
          onPress={handleAddItemPress}
          disabled={disabled}
          {...(Platform.OS === 'web' && {
            role: 'option',
            id: addItemId,
            accessibilityRole: 'button',
            accessibilityState: { disabled },
          })}
        >
          <StyledAddItem focused={isAddItemFocused && Platform.OS === 'web'}>
            <Typography variant="bodyNormal" color="text">
              {addItemLabel}
            </Typography>
          </StyledAddItem>
        </TouchableWithoutFeedback>
      </>
    )
  }, [
    addItemLabel,
    onAddItemPress,
    focusedIndex,
    items.length,
    componentId,
    disabled,
    handleAddItemPress,
  ])

  const dropdownHeight = useMemo(() => {
    const itemCount = items.length + (addItemLabel && onAddItemPress ? 1 : 0)
    return Math.min(itemCount * DROPDOWN_ITEM_HEIGHT, DROPDOWN_MAX_HEIGHT) + 2 // 2px for border
  }, [items.length, addItemLabel, onAddItemPress])

  return (
    <StyledInputContainer testID={testID}>
      {label && (
        <View style={{ flexDirection: 'row' }}>
          <Typography
            variant="bodyNormalBold"
            color={error ? 'error' : 'text'}
            testID="DROPDOWN_LABEL"
            {...(Platform.OS === 'web' && { id: labelId })}
          >
            {label}
          </Typography>
          {showRequiredAsterisk && (
            <Typography variant="bodyNormalBold" color="error" testID="DROPDOWN_LABEL_ASTERISK">
              {' *'}
            </Typography>
          )}
        </View>
      )}
      <DropDownContainerView ref={containerRef} onLayout={handleContainerLayout}>
        <ShadowView>
          <TouchableWithoutFeedback onPress={handleOpen} disabled={disabled}>
            <StyledTriggerContainer
              disabled={disabled}
              error={!!error}
              {...(Platform.OS === 'web' && {
                role: 'combobox',
                'aria-labelledby': label ? labelId : undefined,
                'aria-controls': listboxId,
                'aria-expanded': isExpanded,
                'aria-activedescendant':
                  isExpanded && focusedIndex !== null
                    ? focusedIndex < items.length
                      ? getOptionId(focusedIndex)
                      : `${componentId}-option-add`
                    : undefined,
                tabIndex: disabled ? -1 : 0,
                onKeyDown: handleWebKeyDown,
              })}
            >
              <StyledTriggerText
                hasValue={!!selectedItem}
                error={!!error}
                disabled={disabled}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {displayText}
              </StyledTriggerText>
              <StyledIconContainer disabled={disabled}>
                <Icon
                  src={isExpanded ? 'chevron-up' : 'chevron-down'}
                  variant="material"
                  color={disabled ? 'textDisabled' : 'text'}
                />
              </StyledIconContainer>
            </StyledTriggerContainer>
          </TouchableWithoutFeedback>
        </ShadowView>
      </DropDownContainerView>
      <Infotip
        error={error}
        infotip={infotip}
        isFocused={isFocused}
        isPersistent={isPersistentInfotip}
      />
      {position != null && (
        <Modal
          visible={isExpanded}
          transparent={true}
          animationType="none"
          onRequestClose={handleClose}
        >
          <TouchableWithoutFeedback onPress={handleClose}>
            <StyledDropdownContainer
              ref={dropdownContainerRef}
              {...(Platform.OS === 'web' && {
                tabIndex: -1,
                onKeyDown: handleWebKeyDown,
              })}
            >
              <View
                style={[
                  {
                    position: 'absolute',
                    top: position.y + position.height + 1,
                    left: position.x,
                    width: position.width,
                    height: dropdownHeight,
                  },
                ]}
              >
                <StyledDropdown
                  // @ts-expect-error RN doesn't support this role.
                  role="listbox"
                  {...(Platform.OS === 'web' && {
                    id: listboxId,
                  })}
                >
                  {Platform.OS === 'web' ? (
                    <StyledWebScrollView keyboardShouldPersistTaps="always">
                      {items.map((item, index) => {
                        const info = { item, index } as ListRenderItemInfo<Item>
                        return renderItem(info)
                      })}
                      {renderAddItem()}
                    </StyledWebScrollView>
                  ) : (
                    <FlatList
                      ref={flatListRef}
                      data={items}
                      renderItem={renderItem}
                      keyExtractor={(item: Item) => item.value}
                      keyboardShouldPersistTaps="always"
                      getItemLayout={(_, index) => ({
                        length: DROPDOWN_ITEM_HEIGHT,
                        offset: DROPDOWN_ITEM_HEIGHT * index,
                        index,
                      })}
                      onScrollToIndexFailed={(info) => {
                        const offset = info.index * DROPDOWN_ITEM_HEIGHT
                        flatListRef.current?.scrollToOffset({ offset, animated: true })
                      }}
                      ListFooterComponent={renderAddItem}
                    />
                  )}
                </StyledDropdown>
              </View>
            </StyledDropdownContainer>
          </TouchableWithoutFeedback>
        </Modal>
      )}
    </StyledInputContainer>
  )
}

export default DropDown
